import { GOAL_LIMITS, SKILL_LIST, describeGoal, type GoalKind, type SkillKey } from "@mathtrain/engine";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CoachPlan } from "../components/CoachPlan";
import { api } from "../lib/api";
import { useLoad } from "../lib/hooks";
import type { Growth, PlayRequest } from "../lib/types";

const KINDS: [GoalKind, string][] = [["level", "Reach a level"], ["accuracy", "Accuracy"], ["pace", "Speed"], ["volume", "Practice volume"], ["streak", "Daily streak"]];
const needsSkill = (k: GoalKind) => k === "level" || k === "accuracy" || k === "pace";

export function Grow() {
  const nav = useNavigate();
  const { data, error, reload } = useLoad(() => api<Growth>("/growth"));
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<GoalKind>("level");
  const [skill, setSkill] = useState<SkillKey>("add");
  const [target, setTarget] = useState(3);
  const [msg, setMsg] = useState("");
  const play = (req: PlayRequest) => nav("/play", { state: req });
  if (error) return <main className="page"><div className="error">{error}</div></main>;
  if (!data) return <main className="page"><div className="empty">Loading…</div></main>;

  const create = async (g: { kind: string; skill?: string; target: number }) => {
    setMsg("");
    try { await api<Growth>("/goals", { body: g }); setAdding(false); reload(); }
    catch (e: any) { setMsg(e.message); }
  };
  const remove = async (id: number) => { try { await api(`/goals/${id}`, { method: "DELETE" }); reload(); } catch (e: any) { setMsg(e.message); } };
  const pickKind = (k: GoalKind) => { setKind(k); setTarget(k === "level" ? 3 : k === "accuracy" ? 85 : k === "pace" ? 100 : k === "volume" ? 100 : 7); };
  const lim = GOAL_LIMITS[kind];
  const next = data.nextLessons.map((k) => data.lessons.find((l) => l.key === k)!).filter(Boolean);
  const learned = data.lessons.filter((l) => l.learned).length;

  return <main className="page">
    <h1 style={{ marginTop: 8 }}>Grow</h1>
    <p className="mute" style={{ marginTop: 0 }}>Set goals for yourself, learn techniques, and train what needs it. Everything here measures you against you.</p>

    <h2>Your coach</h2>
    <div className="card">
      <p style={{ margin: "0 0 8px", fontWeight: 600 }}>{data.coach.headline}</p>
      <div className="small mute" style={{ marginBottom: 6 }}>Today's plan</div>
      <CoachPlan plan={data.coach.plan} />
    </div>
    {data.coach.insights.length > 0 && <div className="stack" style={{ marginTop: 12 }}>
      {data.coach.insights.map((i, k) => <div className={"card insight " + i.tone} key={k}>
        <div className="card-title">{i.title}</div>
        <div className="small" style={{ margin: "4px 0" }}>{i.body}</div>
        {i.lesson && <Link className="small" to={`/learn/${i.lesson}`}>Read the strategy</Link>}
      </div>)}
    </div>}

    <h2>Your goals</h2>
    {data.goals.length === 0 && !adding && <div className="empty" style={{ padding: "8px 0" }}>No goals yet. Pick a suggestion below or set your own.</div>}
    {data.goals.map((g) => <div className="card goal" key={g.id}>
      <div className="card-head"><span className="card-title">{g.title}</span>
        <button className="btn quiet" style={{ padding: 0 }} onClick={() => remove(g.id)} aria-label={`Remove goal: ${g.title}`}>Remove</button></div>
      <div className="meter" role="progressbar" aria-valuenow={Math.round(g.fraction * 100)} aria-valuemin={0} aria-valuemax={100}><i style={{ width: `${g.fraction * 100}%` }} /></div>
      <div className="small mute">{g.text}</div>
    </div>)}

    {data.suggestions.length > 0 && <>
      <div className="small mute" style={{ margin: "14px 0 6px" }}>Suggested for you</div>
      {data.suggestions.map((s, i) => <div className="card" key={i}>
        <div className="card-head" style={{ marginBottom: 0 }}><span>{s.title}</span>
          <button className="btn ink" style={{ padding: "8px 14px" }} disabled={data.goals.length >= data.maxGoals} onClick={() => create(s)}>Take it on</button></div>
      </div>)}
    </>}

    {!adding
      ? <button className="btn" style={{ marginTop: 12 }} disabled={data.goals.length >= data.maxGoals} onClick={() => setAdding(true)}>
          {data.goals.length >= data.maxGoals ? `${data.maxGoals} goals is the limit` : "Set your own goal"}</button>
      : <div className="card" style={{ marginTop: 12 }}>
          <div className="seg" role="group" aria-label="Goal type" style={{ flexWrap: "wrap" }}>
            {KINDS.map(([k, label]) => <button key={k} aria-pressed={kind === k} onClick={() => pickKind(k)}>{label}</button>)}
          </div>
          {needsSkill(kind) && <label className="field"><span>Skill</span>
            <select value={skill} onChange={(e) => setSkill(e.target.value as SkillKey)}>
              {data.skills.map((s) => <option key={s.skill} value={s.skill}>{s.name}</option>)}</select></label>}
          <label className="field"><span>{describeGoal({ kind, skill, target }, SKILL_LIST.find((s) => s.key === skill)?.name)}</span>
            <input type="range" min={lim.min} max={lim.max} step={lim.step} value={target} onChange={(e) => setTarget(Number(e.target.value))} /></label>
          <div className="small mute">{kind === "pace" ? "100 means usually finishing within the target time. Lower is faster." : kind === "accuracy" ? "Measured over your last 30 answers in the skill." : kind === "level" ? "Counts from your level today." : ""}</div>
          <div className="grid2" style={{ marginTop: 10 }}>
            <button className="btn" onClick={() => setAdding(false)}>Cancel</button>
            <button className="btn ink" onClick={() => create({ kind, skill: needsSkill(kind) ? skill : undefined, target })}>Add goal</button>
          </div>
        </div>}
    {msg && <div className="error" role="status">{msg}</div>}
    {data.doneGoals.length > 0 && <p className="small mute" style={{ marginTop: 12 }}>Reached: {data.doneGoals.map((g) => g.title).join(" · ")}</p>}

    <h2>Train</h2>
    <p className="mute small" style={{ marginTop: 0 }}>A drill is a fixed set at one level and doesn't change your rating. A trial is ten problems one level up: get 8 right and you move up.</p>
    <div className="stack">
      {data.skills.map((s) => <div className="card" key={s.skill}>
        <div className="card-head"><span className="card-title">{s.name}</span>
          <span className="small mute">level {s.rating.toFixed(1)}{s.accuracy != null ? ` · ${s.accuracy}% right` : ""}{s.pace != null ? ` · ${s.pace}% of target time` : ""}</span></div>
        <div className="grid2">
          <button className="btn" onClick={() => play({ mode: "drill", skill: s.skill, level: Math.max(1, Math.round(s.rating)), length: 10 })}>Drill<small>10 at level {Math.max(1, Math.round(s.rating))}</small></button>
          <button className="btn" disabled={s.trialLevel == null} onClick={() => play({ mode: "drill", skill: s.skill, trial: true })}>Trial<small>{s.trialLevel != null ? `level ${s.trialLevel}, need 8 of 10` : "top level reached"}</small></button>
        </div>
      </div>)}
    </div>

    <h2>Learn</h2>
    {next.length > 0 && <>
      <div className="small mute" style={{ marginBottom: 6 }}>Up next for you</div>
      {next.map((l) => <Link key={l.key} className="btn" to={`/learn/${l.key}`} style={{ marginBottom: 8 }}>{l.title}<small>{l.blurb}</small></Link>)}
    </>}
    <Link className="btn" to="/learn" style={{ marginTop: 6 }}>Browse all strategies<small>{learned} of {data.lessons.length} learned, grouped by kind of calculation</small></Link>
  </main>;
}
